import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import useLocalUserPreferences, { USER_PREFERENCES } from 'hooks/useLocalUserPreferences';

import useMediaRules from 'hooks/useMediaRules';
import { getAdditionalMediaValidators, MEDIA_VALIDATORS } from 'services/FileUtils';
import { allowedFileValidator, getError } from 'ui/base/MediasUploader/utils';
import useNotification from 'ui/base/Notification';
import { b } from 'utils/i18nUtils';

import DraggerContent from './DraggerContent';
import EditDrawer from './EditDrawer';
import FromLibrary from './FromLibrary';
import ImportedMediaList from './ImportedMediaList';
import {
  Panel,
  Error,
  Dragger,
  TakePicture,
  MaskContent,
  DraggerWrapper,
  ContentWrapper,
  RadioGroupUpload,
  ImportedFileSection,
} from './UI';

import { DISPLAY_TYPES, DISPLAY_TYPES_NAME, format } from './utils';
import getVideoDuration from './utils/getVideoDuration';
import PhotoMaker from '../../PhotoMaker';

/**
 * @typedef {{
 * limit: number,
 * autoBrowse: boolean,
 * showDrawer: boolean,
 * uploadInLibrary: boolean,
 * disabledAddMedia: boolean,
 * displayMaxSizeMessage: boolean,
 * tabs: import('./utils').DISPLAY_TYPES,
 * onDrawerOpen: (value: boolean) => void,
 * allowed: import("hooks/useMediaRules").AllowedMedias,
 * onChangeTabs: (value: import('./utils').DISPLAY_TYPES) => void,
 * medias: import('modules/mediaLibrary/types').UploadModalMedia[],
 * mediasErrors: Object | undefined,
 * onChangeMedias: (value: import('modules/mediaLibrary/types').UploadModalMedia[]) => void,
 * }} ContentProps
 */

/** @type {React.FC<ContentProps>} */
const Content = ({
  allowed,
  limit,
  tabs,
  medias,
  mediasErrors,
  autoBrowse,
  showDrawer,
  displayTypes,
  onChangeTabs,
  onDrawerOpen,
  onChangeMedias,
  uploadInLibrary,
  displayMaxSizeMessage,
  disabledAddMedia,
}) => {
  const intl = useIntl();
  const draggerRef = React.useRef(undefined);
  const notification = useNotification();
  const { config, extensions, mediaTypes, fileTypes } = useMediaRules(allowed);

  const { setPreference, [USER_PREFERENCES.MEDIA_LIBRARY]: library } = useLocalUserPreferences();

  const [mediaId, setMediaId] = React.useState(null);
  const [hasWebcamError, setWebcamError] = React.useState(false);

  const isRetry = medias.some(media => media.isUploaded === false);

  const openFileExplorer = useDebouncedCallback(() => {
    draggerRef.current?.browse?.();
  }, 50);

  React.useEffect(() => {
    if (tabs === DISPLAY_TYPES.COMPUTER && autoBrowse) {
      openFileExplorer();
    }
  }, []);

  const handleSetDisplay = React.useCallback(({ target: { value } }) => onChangeTabs(value), [onChangeTabs]);

  const setLibrary = React.useCallback(
    newLibrary => setPreference(USER_PREFERENCES.MEDIA_LIBRARY, newLibrary),
    [setPreference]
  );

  const debouncedError = useDebouncedCallback(error => notification.error({ title: error }), 250);

  const mediaLimitValidator = React.useCallback(
    (_, fileList) => {
      if (fileList.length + medias.length > limit) {
        return intl.formatMessage({
          id: 'mediaLibrary.errorMaxMedia',
          defaultMessage: 'You have exceeded the maximum number of files to import',
        });
      }
      return undefined;
    },
    [intl, limit, medias.length]
  );

  const handleOnGenerateMedia = React.useCallback(
    /** @param {File} file
     * @param {File[]} [fileList]
     * @param {boolean} [preRecorded]
     */
    async (file, fileList, preRecorded) => {
      const additionalValidators = getAdditionalMediaValidators(allowed);

      const error = await getError(file, [
        () => allowedFileValidator(file, intl, extensions, fileTypes),
        () => mediaLimitValidator(undefined, fileList ?? [file]),
        ...additionalValidators.map(validator => MEDIA_VALIDATORS[validator](config, intl)),
      ]);

      if (!error) {
        const time = await getVideoDuration(file);
        time && Object.assign(file, { time });
        onChangeMedias(selectedMedias => [...selectedMedias, format(file, uploadInLibrary, preRecorded)]);
      } else {
        debouncedError(error);
      }
    },
    [allowed, intl, extensions, fileTypes, mediaLimitValidator, config, getVideoDuration, onChangeMedias, uploadInLibrary, debouncedError]
  );

  const onBeforeUpload = React.useCallback(
    /** @param {File} file
     * @param {File[]} fileList
     */
    async (file, fileList) => {
      await handleOnGenerateMedia(file, fileList);
      // return a rejected Promise to stop the uploader.
      return Promise.reject();
    },
    [handleOnGenerateMedia]
  );

  const handleOnRecordMedia = React.useCallback(
    /** @param {'picture' | 'video'} _mode
     * @param {File} file
     */
    (_mode, file) => handleOnGenerateMedia(file, undefined, false),
    [handleOnGenerateMedia]
  );

  const handleDeleteMedia = id => onChangeMedias(selectedMedias => selectedMedias.filter(media => media.id !== id));

  const handleOnError = React.useCallback(() => {
    setWebcamError(true);
    notification.error({
      title: intl.formatMessage({ id: 'error.label', defaultMessage: 'Error' }),
      description: intl.formatMessage({
        id: 'cameraDevice.error',
        defaultMessage: 'Please be sure that you <b>have a camera</b> and <b>granted camera and microphone</b> access',
      }, { b },),
    });
  }, [intl, notification]);

  const handleCloseDrawer = () => onDrawerOpen(false);

  const handleUpdateMedia = id => {
    onDrawerOpen(true);
    setMediaId(id);
  };

  const editedMedia = React.useMemo(() => medias.find(media => media.id === mediaId), [mediaId, medias]);

  const handleMediaUpdate = media => {
    const ext = media.filename?.split('.').pop();
    const correctedFile = media.name && media.file instanceof File
      ? new File([media.file], `${media.name}.${ext}`, { type: media.file.type })
      : media.file;
    onChangeMedias(selectedMedias => selectedMedias.map(m => (m.id === media.id ? { ...media, file: correctedFile } : m)));
    handleCloseDrawer();
  };

  const radioGroupOptions = React.useMemo(
    () =>
      displayTypes.reduce((acc, type) => {
        const exists = Object.values(DISPLAY_TYPES).includes(type);
        if (exists) {
          acc.push({
            value: type,
            label: <span data-cy={`uploadModal-${type.toLowerCase()}`}>{DISPLAY_TYPES_NAME[type]}</span>,
          });
        }
        return acc;
      }, []),
    [displayTypes]
  );

  return (
    <>
      <ContentWrapper>
        <Panel>
          {isRetry && (
            <MaskContent>
              <Error>
                <i className="fb-icon--status-urgent" />
                <FormattedMessage id="mediaLibrary.mediaFailedWarning" defaultMessage="Some media failed to import" />
              </Error>
            </MaskContent>
          )}
          {!uploadInLibrary && (
            <RadioGroupUpload
              value={tabs}
              optionType="button"
              buttonStyle="solid mb-2"
              options={radioGroupOptions}
              onChange={handleSetDisplay}
            />
          )}
          {tabs === DISPLAY_TYPES.COMPUTER && (
            <DraggerWrapper>
              <Dragger
                allowed={allowed}
                multiple={limit > 1}
                disabled={disabledAddMedia}
                beforeUpload={onBeforeUpload}
                data-cy="mediaLibrary-uploader"
              >
                <DraggerContent
                  config={config}
                  extensions={extensions}
                  disabled={disabledAddMedia}
                  ref={draggerRef}
                  displayMaxSizeMessage={displayMaxSizeMessage}
                />
              </Dragger>
            </DraggerWrapper>
          )}
          {tabs === DISPLAY_TYPES.LIBRARY && (
            <FromLibrary
              library={library}
              selectedMedias={medias}
              onSelect={onChangeMedias}
              onLibraryChange={setLibrary}
              disabledAddMedia={disabledAddMedia}
              filters={{ types: mediaTypes }}
            />
          )}
          {tabs === DISPLAY_TYPES.TAKE_PICTURE && (
            <TakePicture>
              {hasWebcamError && (
                <Error>
                  <i className="fb-icon--status-urgent" />
                  <FormattedMessage
                    id="cameraDevice.error"
                    defaultMessage="Please be sure that you <b>have a camera</b> and <b>granted camera and microphone</b> access"
                    values={{ b }}
                  />
                </Error>
              )}
              <PhotoMaker
                onError={handleOnError}
                disabled={disabledAddMedia || hasWebcamError}
                onGenerateMedia={handleOnRecordMedia}
              />
            </TakePicture>
          )}
        </Panel>
        <ImportedFileSection>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <span className="title">
              <FormattedMessage id="mediaLibrary.importedFiles" defaultMessage="Imported files" /> ({medias.length})
            </span>
            <span className="media-max" data-cy="media-limit">
              <FormattedMessage
                id="mediaLibrary.mediaMax"
                defaultMessage="{nbMedia}/{nbMediaMax} media max."
                values={{ nbMedia: medias.length, nbMediaMax: limit }}
              />
            </span>
          </div>
          <ImportedMediaList medias={medias} mediasErrors={mediasErrors} onUpdate={handleUpdateMedia} onDelete={handleDeleteMedia} />
        </ImportedFileSection>
      </ContentWrapper>
      {showDrawer && (
        <EditDrawer
          media={editedMedia}
          onSave={handleMediaUpdate}
          onClose={handleCloseDrawer}
          showAddToLibrary={!uploadInLibrary}
        />
      )}
    </>
  );
};

export default Content;
